import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import styles from "./importData.module.css";
import { listFiles } from "../../../services/import";
import { useNotificationAction } from "../../../context/NotificationActionContext";
import { useImportFlow } from "../../../context/ImportFlowContext";
import useImportNotifications from "../../../hooks/useImportNotifications";
import Button from "../../../components/button/button";
import TargetIcon from "../../../assets/icons/target.svg";
import BookIcon from "../../../assets/icons/book.svg";
import ClassesIcon from "../../../assets/icons/classes.svg";
import DownloadIcon from "../../../assets/icons/download.svg";
import useOutsideAlerter from "../../../utils/clickOutside";
import UsersTwoIcon from "../../../assets/icons/users-two.svg";
import FileExportIcon from "../../../assets/icons/file-export.svg";
import { useInstitution } from "../../../context/InstitutionContext";
import ChartMoneyIcon from "../../../assets/icons/chart-money.svg";
import DocumentCheckIcon from "../../../assets/icons/document-check.svg";
import CurrencyDollarIcon from "../../../assets/icons/currency-dollars.svg";
import HeaderTitle from "../../../components/header/headerTitle/headerTitle";
import ImportDataTable from "../../../components/importData/importDataTable/importDataTable";
import ImportDataModal from "../../../components/importData/importDataModal/importDataModal";

const ImportData = () => {
  const { institution } = useInstitution();
  const currentInstitution = institution;

  const navigate = useNavigate();
  const { pendingAction, clearAction } = useNotificationAction();
  const { setImportData } = useImportFlow();

  const sendFilePopoverRef = useRef(null);

  const [files, setFiles] = useState([]);

  const [showUpload, setShowUpload] = useState(false);
  const [fileModelType, setFileModelType] = useState("");
  const [sendFilePopover, setSendFilePopover] = useState(false);

  useOutsideAlerter(sendFilePopoverRef, () => setSendFilePopover(false));

  const fetchFiles = useCallback(async () => {
    const response = await listFiles(currentInstitution?.id);
    setFiles(response);
  }, [currentInstitution?.id]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  // SignalR: recebe notificações em tempo real quando importações em background finalizam
  useImportNotifications(currentInstitution?.id, useCallback(() => {
    fetchFiles();
  }, [fetchFiles]));

  // Notificação FIELD_MAPPING_REQUIRED: carrega o estado pausado na tela de revisão (modo resume,
  // sem o arquivo em memória — a importação retoma pelo correlationId) e navega para o mapeamento.
  useEffect(() => {
    if (pendingAction?.type !== "FIELD_MAPPING_REQUIRED") return;

    const data = pendingAction.data || {};
    setImportData({
      smart: true,
      correlationId: data.correlationId,
      institutionId: currentInstitution?.id,
      fileName: data.fileName,
      headerSpec: data.headerSpec ?? null,
      notificationId: pendingAction.notificationId,
      detection: {
        detectedTypes: data.detectedTypes || [],
        receivedHeaders: data.receivedHeaders || [],
        sampleRows: data.sampleRows || [],
        unmappedColumns: data.unmappedColumns || [],
        rowCount: data.rowCount,
        notice: data.notice,
        source: data.source || "ai"
      }
    });
    clearAction();
    navigate("/app/importar-dados/revisao-inteligente");
  }, [pendingAction, clearAction, setImportData, navigate, currentInstitution?.id]);

  const toogleUploadModal = (status, type) => {
    setFileModelType(type);
    setSendFilePopover(false);
    setShowUpload(status);
  };

  const handleCloseModal = () => {
    fetchFiles();
    setShowUpload(false)
  }

  const handleDownloadAll = () => {
    const modelFiles = [
      { name: "academico.csv", type: "Acadêmico" },
      { name: "adesao.csv", type: "Adesão" },
      { name: "aluno.csv", type: "Alunos" },
      { name: "captacao.csv", type: "Captação" },
      { name: "docente.csv", type: "Docentes" },
      { name: "faturamento.csv", type: "Faturamento" },
      { name: "turma.csv", type: "Turmas" }
    ];

    modelFiles.forEach((file) => {
      const link = document.createElement("a");
      link.href = `/model-files/${file.name}`;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });

    setSendFilePopover(false);
  }

  return (
    <div>
      <HeaderTitle title="Importar Dados">
        <div className={styles.headerActions}>
          <div style={{ position: "relative" }} className="import-data-button">
            <Button
              icon={FileExportIcon}
              text="Enviar Arquivo"
              size="normal"
              variation="primary"
              onClick={() => {
                setSendFilePopover(!sendFilePopover);
              }}
            />

          {sendFilePopover && (
            <div ref={sendFilePopoverRef} className={styles.popover}>
              <ul className={styles.popoverContent}>
                <li onClick={() => toogleUploadModal(true, "inteligente")}>
                  <img src={FileExportIcon} alt="Importação Inteligente" />
                  <span>Importação Inteligente</span>
                </li>

                <li onClick={() => toogleUploadModal(true, "academico")}>
                  <img src={CurrencyDollarIcon} alt="Users Two" />
                  <span>Acadêmico</span>
                </li>

                <li onClick={() => toogleUploadModal(true, "adesao")}>
                  <img src={DocumentCheckIcon} alt="Users Two" />
                  <span>Adesão</span>
                </li>

                <li onClick={() => toogleUploadModal(true, "aluno")}>
                  <img src={UsersTwoIcon} alt="Users Two" />
                  <span>Alunos</span>
                </li>

                <li onClick={() => toogleUploadModal(true, "captacao")}>
                  <img src={TargetIcon} alt="Users Two" />
                  <span>Captação</span>
                </li>

                <li onClick={() => toogleUploadModal(true, "docente")}>
                  <img src={BookIcon} alt="Docentes" />
                  <span>Docentes</span>
                </li>

                <li onClick={() => toogleUploadModal(true, "faturamento")}>
                  <img src={ChartMoneyIcon} alt="Users Two" />
                  <span>Faturamento</span>
                </li>

                {/* <li onClick={() => toogleUploadModal(true, "financeiro")}>
                  <img src={CurrencyDollarIcon} alt="Users Two" />
                  <span>Financeiro</span>
                </li> */}

                <li onClick={() => toogleUploadModal(true, "turma")}>
                  <img src={ClassesIcon} alt="Users Two" />
                  <span>Turmas</span>
                </li>

                <li onClick={handleDownloadAll} className={styles.downloadAllItem}>
                  <img src={DownloadIcon} alt="Baixar todos os exemplos" />
                  <span>Baixar Todos os Exemplos</span>
                </li>
              </ul>
            </div>
          )}
          </div>
        </div>
      </HeaderTitle>

      <div className="mainContentSpace">
        <ImportDataTable files={files} onRefresh={fetchFiles} />
      </div>

      {showUpload && (
        <ImportDataModal
          fileType={fileModelType}
          onClose={() => handleCloseModal()}
          institutionId={currentInstitution?.id}
        />
      )}

    </div>
  );
};

export default ImportData;
